import { useEffect, useState, useMemo } from "react";

import { useNavigate } from "react-router";

import { FormProvider, useForm } from "react-hook-form";

import { CampaignDetailsView } from "../campaignDetailsView/CampaignDetailsView.tsx";
import { CampaignFormView } from "../campaignFormView/CampaignFormView.tsx";
import { RevButton, RevUnsavedChangesDialog } from "@revmatics/catalyst";
import {
  CampaignByIdEditBodyType,
  CampaignByIdItemDataType,
  RecommendationsItemDataType,
} from "../../../../../../../../stores/types/campaignsTypes.tsx";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../../stores/hooks/hooks.ts";
import {
  createCampaign,
  editCampaignByIdData,
} from "../../../../../../../../stores/thunks/campaignsThunk.tsx";
import { useUnsavedChangesDialog } from "../../../../../../../../helpers/hooks/useUnsavedChangesDialog.tsx";

type Props = {
  campaignById: CampaignByIdItemDataType | null;
  initRecommendation: RecommendationsItemDataType[] | null;
  setIsEditMode: (value: boolean) => void;
  isEditMode: boolean;
  create?: boolean;
};

export const CreateEditDetailCampaign = ({
  campaignById,
  initRecommendation,
  setIsEditMode,
  isEditMode,
  create,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isOpenUnsavedModal, setOpenUnsavedModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [recommendations, setRecommendations] = useState<
    RecommendationsItemDataType[]
  >([]);
  const [href, setHref] = useState("");

  const isLoadingByIdEdit = useAppSelector(
    (state) => state.campaigns.isLoadingByIdEdit,
  );
  const isLoadingCreate = useAppSelector(
    (state) => state.campaigns.isLoadingCreate,
  );

  const initRecommendationList = useMemo(() => {
    if (!initRecommendation?.length) return [];
    return initRecommendation.map((item) => ({
      ...item,
      recommendation:
        item.type === "ad_platform_mapping"
          ? "no_preference"
          : "do_not_include",
    }));
  }, [initRecommendation]);

  useEffect(() => {
    if (campaignById) {
      if (campaignById.time_frame.start)
        setStartDate(campaignById.time_frame.start);
      if (campaignById.time_frame.end) setEndDate(campaignById.time_frame.end);
      setRecommendations(
        campaignById.recommendations?.length
          ? campaignById.recommendations
          : initRecommendationList,
      );
    } else {
      setRecommendations(initRecommendationList);
    }
  }, [campaignById, initRecommendationList]);

  const methods = useForm<CampaignByIdEditBodyType>({
    mode: "onSubmit",
    defaultValues: {
      name: "",
      kpi: "",
      kpi_value: undefined,
      start_date: "",
      end_date: "",
      budget: "",
      recommendations: initRecommendationList,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (campaignById) {
      reset({
        name: campaignById.name || "",
        kpi: campaignById.kpi || "",
        kpi_value: campaignById.kpi_value || undefined,
        start_date: campaignById.time_frame.start || "",
        end_date: campaignById.time_frame.end || "",
        budget: campaignById.total_budget
          ? Number(campaignById.total_budget).toFixed(0).toString()
          : "",
        recommendations: campaignById.recommendations?.length
          ? campaignById.recommendations
          : initRecommendationList,
      });
    } else {
      reset({
        name: "",
        kpi: "",
        kpi_value: undefined,
        start_date: "",
        end_date: "",
        budget: "",
        recommendations: initRecommendationList,
      });
    }
  }, [campaignById, initRecommendationList, reset]);

  const handleDiscardChanges = () => {
    setOpenUnsavedModal(false);

    // If we have a stored destination from the hook, go there.
    // Otherwise, default to the previous page (navigate -1).
    if (href) {
      navigate(href);
    } else {
      navigate(-1);
    }
    setHref("");
  };

  const onSubmit = (data: CampaignByIdEditBodyType) => {
    console.log("here");
    const body = {
      ...data,
      kpi_value: data.kpi_value || null,
      recommendations,
      start_date: startDate,
      end_date: endDate,
    };
    if (campaignById) {
      dispatch(
        editCampaignByIdData({
          id: campaignById.id.toString(),
          body,
          callback: () => {
            if (href) {
              navigate(href);
            } else {
              setIsEditMode(false);
            }
          },
        }),
      );
    } else {
      dispatch(
        createCampaign({
          body,
          callback: () => {
            if (href) {
              navigate(href);
            } else {
              navigate("/campaigns");
            }
          },
        }),
      );
    }
  };

  const hasUnsavedChanges =
    isDirty ||
    (campaignById
      ? startDate !== campaignById?.time_frame.start ||
        endDate !== campaignById?.time_frame.end ||
        JSON.stringify(recommendations) !==
          JSON.stringify(
            campaignById?.recommendations &&
              campaignById?.recommendations.length > 0
              ? campaignById?.recommendations
              : initRecommendationList,
          )
      : !!startDate || !!endDate || recommendations.length > 0);

  useUnsavedChangesDialog({
    hasUnsavedChanges,
    onNavigateAttempt: (href) => {
      setHref(href);
      setOpenUnsavedModal(true);
    },
  });

  return (
    <div className="min-h-full bg-primary-25">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="pt-4 pb-8 px-6">
            <div>
              {!isEditMode && campaignById ? (
                <CampaignDetailsView campaignById={campaignById} />
              ) : (
                <CampaignFormView
                  campaignById={campaignById}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  startDate={startDate}
                  endDate={endDate}
                  recommendations={recommendations}
                  setRecommendations={setRecommendations}
                />
              )}

              <RevUnsavedChangesDialog
                pageId="create-edit-campaign-page"
                isOpen={isOpenUnsavedModal}
                setIsOpen={setOpenUnsavedModal}
                onSave={handleSubmit(onSubmit)}
                onDiscard={handleDiscardChanges}
                isLoading={isLoadingByIdEdit || isLoadingCreate}
                classNameContainer="z-50"
              />
            </div>
          </div>
          {(isEditMode || create) && (
            <div className="grid grid-cols-2 sm:flex sm:justify-end gap-3 sticky bottom-0 px-6 py-4 bg-white shadow-elevation--1 z-10">
              <RevButton
                type="button"
                text="Cancel"
                variant="secondary"
                buttonSize="medium"
                handleClick={() => {
                  if (hasUnsavedChanges) {
                    setOpenUnsavedModal(true);
                    return;
                  }
                  reset();
                  setIsEditMode(false);
                }}
                className="z-10"
              />
              <RevButton
                type="submit"
                text="Save"
                buttonSize="medium"
                variant="primary"
                isLoading={isLoadingByIdEdit || isLoadingCreate}
                className="z-10"
              />
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
